import {createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode} from 'react';
import {ChatFeedbackListSchema, ChatFeedbackMessageKeySchema, ChatFeedbackResultSchema, type ChatFeedbackRating} from '../src/chat-feedback-types';
import {ErrorNotice, Loading} from './components';
import {errorDetails, request, useHermes} from './lib/store';
import type {ErrorDetails} from './lib/types';
import './chat-message-actions.css';

type FeedbackContextValue = {
  ready: boolean;
  ratings: ReadonlyMap<string, ChatFeedbackRating>;
  pending: ReadonlySet<string>;
  save: (nativeSessionId: string, messageRowId: number, messageKey: string, rating: ChatFeedbackRating | null) => Promise<void>;
};
const FeedbackContext = createContext<FeedbackContextValue | null>(null);
const feedbackKey = (nativeSessionId: string, messageKey: string): string => JSON.stringify([nativeSessionId, messageKey]);
const messageTime = new Intl.DateTimeFormat(undefined, {weekday: 'long', hour: '2-digit', minute: '2-digit'});

/** One read for the whole work chat, shared by head and specialist message actions. */
export function ChatFeedbackProvider({sessionId, children}: {sessionId: string; children: ReactNode}) {
  const {state} = useHermes();
  const session = state.session.data;
  if (!session) return <>{children}</>;
  return <FeedbackScope key={`${session.accountId}:${session.csrfToken}:${sessionId}`} sessionId={sessionId} csrf={session.csrfToken}>
    {children}
  </FeedbackScope>;
}

function FeedbackScope({sessionId, csrf, children}: {sessionId: string; csrf: string; children: ReactNode}) {
  const [ratings, setRatings] = useState<ReadonlyMap<string, ChatFeedbackRating>>(new Map());
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState<ErrorDetails | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const inFlight = useRef(new Set<string>());
  const mounted = useRef(true);
  const path = `/hermes/sessions/${encodeURIComponent(sessionId)}/feedback`;
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    void request(path, {signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30_000)])}).then(raw => {
      const result = ChatFeedbackListSchema.parse(raw);
      if (result.sessionId !== sessionId) throw new Error('Feedback was returned for another conversation.');
      if (!controller.signal.aborted) setRatings(new Map(result.feedback.map(item => [feedbackKey(item.nativeSessionId, item.messageKey), item.rating])));
    }).catch(error => {
      if (!controller.signal.aborted) setReadError(errorDetails(error));
    }).finally(() => {if (!controller.signal.aborted) setLoading(false);});
    return () => {mounted.current = false; controller.abort();};
  }, [path, sessionId]);

  const save = useCallback<FeedbackContextValue['save']>(async (nativeSessionId, messageRowId, messageKey, rating) => {
    if (loading || readError) throw new Error('Message feedback is unavailable for this conversation.');
    const key = feedbackKey(nativeSessionId, messageKey);
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setPending(new Set(inFlight.current));
    try {
      const result = ChatFeedbackResultSchema.parse(await request(`${path}/${messageRowId}`, {method: 'PUT', csrf,
        body: {rating, ...(nativeSessionId === sessionId ? {} : {agentId: nativeSessionId})}}));
      if (result.nativeSessionId !== nativeSessionId || result.messageRowId !== messageRowId || result.messageKey !== messageKey || result.rating !== rating) {
        throw new Error('The saved feedback does not match this assistant reply.');
      }
      if (mounted.current) setRatings(current => {
        const next = new Map(current);
        if (messageKey.startsWith('logical:')) next.delete(feedbackKey(nativeSessionId, `row:${messageRowId}`));
        if (result.rating === null) next.delete(key);
        else next.set(key, result.rating);
        return next;
      });
    } finally {
      inFlight.current.delete(key);
      if (mounted.current) setPending(new Set(inFlight.current));
    }
  }, [loading, readError, path, csrf, sessionId]);
  const value = useMemo(() => ({ready: !loading && !readError, ratings, pending, save}), [loading, readError, ratings, pending, save]);
  return <FeedbackContext.Provider value={value}>
    <Loading active={loading}/>
    <ErrorNotice error={readError} title="Message feedback could not be loaded"/>
    {children}
  </FeedbackContext.Provider>;
}

function ActionIcon({kind}: {kind: 'copy' | 'up' | 'down'}) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {kind === 'copy' ? <><rect x="8" y="3" width="12" height="14" rx="2"/><path d="M16 17v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3"/></>
      : <g transform={kind === 'down' ? 'rotate(180 12 12)' : undefined}>
        <path d="M8 10l4-7c2 0 3 1 2 4l-.7 3H19a2 2 0 0 1 2 2.4l-1.3 6A2 2 0 0 1 17.7 20H8z"/>
        <rect x="3" y="10" width="5" height="10" rx="1"/>
      </g>}
  </svg>;
}

/** Live replies can be copied; votes appear only after Hermes saves a native row. */
export const NativeMessageActions = memo(function NativeMessageActions({nativeSessionId, messageRowId, messageKey: suppliedMessageKey = null, text, timestamp = null}: {
  nativeSessionId: string; messageRowId: number | null; messageKey?: string | null; text: string; timestamp?: number | null;
}) {
  const feedback = useContext(FeedbackContext);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const saved = messageRowId !== null && Number.isSafeInteger(messageRowId) && messageRowId > 0;
  const messageKey = saved ? ChatFeedbackMessageKeySchema.parse(suppliedMessageKey ?? `row:${messageRowId}`) : null;
  const key = messageKey === null ? null : feedbackKey(nativeSessionId, messageKey);
  const rating = key === null ? undefined : feedback?.ratings.get(key)
    ?? feedback?.ratings.get(feedbackKey(nativeSessionId, `row:${messageRowId}`));
  const pending = key !== null && feedback?.pending.has(key) === true;
  const date = timestamp === null ? null : new Date(timestamp * 1000);
  const copy = async () => {
    setError(null);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Copying requires a browser clipboard connection. Select the reply text to copy it.');
      await navigator.clipboard.writeText(text);
    } catch (reason) {setError(errorDetails(reason));}
  };
  const vote = async (value: ChatFeedbackRating) => {
    if (!feedback || !saved || messageKey === null) return;
    setError(null);
    try {await feedback.save(nativeSessionId, messageRowId, messageKey, rating === value ? null : value);}
    catch (reason) {setError(errorDetails(reason));}
  };
  return <div className="chat-message-actions-wrap">
    <div className="chat-message-actions" role="group" aria-label="Reply actions">
      <button type="button" className="chat-message-action" aria-label="Copy reply" title="Copy reply" onClick={() => void copy()}><ActionIcon kind="copy"/></button>
      {saved && feedback && <>
        <button type="button" className="chat-message-action" aria-label="Good response" title="Good response" aria-pressed={rating === 'up'}
          disabled={!feedback.ready || pending} onClick={() => void vote('up')}><ActionIcon kind="up"/></button>
        <button type="button" className="chat-message-action" aria-label="Bad response" title="Bad response" aria-pressed={rating === 'down'}
          disabled={!feedback.ready || pending} onClick={() => void vote('down')}><ActionIcon kind="down"/></button>
      </>}
      {date && Number.isFinite(date.getTime()) && <time dateTime={date.toISOString()}>{messageTime.format(date)}</time>}
    </div>
    <ErrorNotice error={error}/>
  </div>;
});
